import { useEffect, useMemo, useState } from "react";
import { Avatar, Button, Empty, Modal, Segmented, Select, Spin, Tag, Tooltip } from "antd";
import {
  CheckCircleOutlined,
  EditOutlined,
  ReloadOutlined,
  UserOutlined,
  UserSwitchOutlined,
} from "../icons";
import { ScriptSheetCellContent } from "./ScriptSheetCellContent";
import {
  ScriptMemberPickerModal,
  ScriptMemberProfile,
} from "./ScriptMemberPickerModal";
import { useScriptAssignmentReview } from "../../hooks/scriptSheet/useScriptAssignmentReview";
import type { ScriptAssignmentReviewConfirmResult } from "../../hooks/scriptSheet/useScriptAssignmentReview";
import type { AssignmentResult } from "../../hooks/scriptSheet/types";
import {
  hasScriptSheetContentInRow,
  isScriptAtmosphereRole,
  isScriptDispatcherRole,
  type ParsedScriptSheetRow,
} from "../../../shared/feishuScriptSheet";
import { buildAssignmentRowEligibility } from "../../../shared/feishuScriptAssignment";
import { getScriptContentBeatLabel } from "../../../shared/scriptRoleDiversity";
import type { RoleAssignmentItem } from "../../../shared/scriptRoleAssignment";
import type { ScriptRoleReviewMemberSnapshot } from "../../../shared/scriptRoleReview";
import "./scriptAssignmentReview.css";

type ReviewView = "all" | "changed";

type ReviewTimelineEntry =
  | {
      kind: "role";
      key: string;
      tempRoleId: string;
      rows: ParsedScriptSheetRow[];
    }
  | {
      kind: "context";
      key: string;
      role: string;
      rows: ParsedScriptSheetRow[];
    };

export interface ScriptAssignmentReviewPanelProps {
  open: boolean;
  assignmentResult: AssignmentResult;
  parsedRows: ParsedScriptSheetRow[];
  sourceFingerprint: string;
  sheetTitle: string;
  overwriteMode: boolean;
  imageTextsByFileToken: Record<string, string>;
  onClose: () => void;
  onConfirmStart?: () => void;
  onConfirmed: (result: ScriptAssignmentReviewConfirmResult) => void;
  onConfirmError?: (error: Error) => void;
}

function resolveRoleIdByRow(input: {
  assignmentResult: AssignmentResult;
  parsedRows: ParsedScriptSheetRow[];
  overwriteMode: boolean;
}) {
  const roleIds = new Set(input.assignmentResult.tempRoles.map((role) => role.tempRoleId));
  const eligibility = buildAssignmentRowEligibility(input.parsedRows, {
    overwriteMode: input.overwriteMode,
  });
  const explicitRoleIdByRowNumber = new Map<number, string>();

  for (const role of input.assignmentResult.tempRoles) {
    for (const rowNumber of role.contentRowNumbers ?? []) {
      explicitRoleIdByRowNumber.set(rowNumber, role.tempRoleId);
    }
  }

  return (row: ParsedScriptSheetRow) => {
    const explicit = explicitRoleIdByRowNumber.get(row.rowNumber);
    if (explicit) return explicit;

    const rowEligibility = eligibility.get(row.rowIndex);
    const eligibleRoleId = rowEligibility?.eligible ? rowEligibility.tempRoleId : undefined;
    if (eligibleRoleId && roleIds.has(eligibleRoleId)) return eligibleRoleId;

    if (!rowEligibility?.eligible) return undefined;

    return input.assignmentResult.tempRoles.find((role) => {
      if (role.contentRowNumbers?.length) return false;
      const lastRowIndex = role.lastRowIndex ?? role.firstRowIndex;
      return row.rowIndex >= role.firstRowIndex && row.rowIndex <= lastRowIndex;
    })?.tempRoleId;
  };
}

export function buildReviewTimeline(input: {
  assignmentResult: AssignmentResult;
  parsedRows: ParsedScriptSheetRow[];
  overwriteMode: boolean;
}): ReviewTimelineEntry[] {
  const rows = input.parsedRows
    .filter(hasScriptSheetContentInRow)
    .sort((left, right) => left.rowIndex - right.rowIndex);
  const resolveRoleId = resolveRoleIdByRow(input);
  const reviewRoleIds = new Set(input.assignmentResult.tempRoles.map((role) => role.tempRoleId));
  const entries: ReviewTimelineEntry[] = [];
  const representedRoleIds = new Set<string>();

  for (const row of rows) {
    const tempRoleId = isScriptAtmosphereRole(row.role) ? resolveRoleId(row) : undefined;
    if (tempRoleId && reviewRoleIds.has(tempRoleId)) {
      representedRoleIds.add(tempRoleId);
      const previous = entries.at(-1);
      if (previous?.kind === "role" && previous.tempRoleId === tempRoleId) {
        previous.rows.push(row);
      } else {
        entries.push({
          kind: "role",
          key: `role-${tempRoleId}-${row.rowIndex}`,
          tempRoleId,
          rows: [row],
        });
      }
      continue;
    }

    const previous = entries.at(-1);
    if (previous?.kind === "context" && previous.role === row.role) {
      previous.rows.push(row);
    } else {
      entries.push({
        kind: "context",
        key: `context-${row.rowIndex}`,
        role: row.role || "未标注角色",
        rows: [row],
      });
    }
  }

  for (const role of input.assignmentResult.tempRoles) {
    if (representedRoleIds.has(role.tempRoleId)) continue;
    entries.push({
      kind: "role",
      key: `role-${role.tempRoleId}-missing`,
      tempRoleId: role.tempRoleId,
      rows: [],
    });
  }

  return entries;
}

function ScriptRows(props: {
  rows: ParsedScriptSheetRow[];
  imageTextsByFileToken: Record<string, string>;
}) {
  const { rows, imageTextsByFileToken } = props;
  if (rows.length === 0) {
    return <Empty description="当前角色没有可展示的剧本行，请勿确认并联系管理员检查数据" />;
  }

  return (
    <div className="script-review-lines">
      {rows.map((row) => {
        const imageToken = row.cellImages.C;
        const roleImageToken = row.roleImageFileToken || row.cellImages.B;
        return (
          <div className="script-review-line" key={row.rowIndex}>
            <div className="script-review-line-number">{row.rowNumber}</div>
            <div className="script-review-role-image">
              {roleImageToken ? (
                <ScriptSheetCellContent imageFileToken={roleImageToken} maxImageSize={48} />
              ) : null}
            </div>
            <div className="script-review-line-content">
              <ScriptSheetCellContent
                text={row.cells.C}
                imageFileToken={imageToken}
                imageUnderstanding={imageToken ? imageTextsByFileToken[imageToken] : undefined}
                maxImageSize={260}
              />
            </div>
            <div className="script-review-line-existing">
              <span>D 列</span>
              <strong>{row.cells.D || "空"}</strong>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MissingMember(props: {
  wxid: string;
  originalAssignment?: RoleAssignmentItem;
}) {
  const { wxid, originalAssignment } = props;
  return (
    <div className="script-review-member-missing">
      <Avatar size={48} icon={<UserOutlined />} />
      <div>
        <strong>
          {originalAssignment?.number || "无编号"} {originalAssignment?.nickname || wxid}
        </strong>
        <span>{wxid}</span>
      </div>
      <Tag color="red">已不在当前分组</Tag>
    </div>
  );
}

function ContextEntry(props: {
  entry: Extract<ReviewTimelineEntry, { kind: "context" }>;
  imageTextsByFileToken: Record<string, string>;
}) {
  const { entry, imageTextsByFileToken } = props;
  const dispatcher = isScriptDispatcherRole(entry.role);
  return (
    <article className={`script-review-context${dispatcher ? " is-dispatcher" : ""}`}>
      <header>
        <Tag color={dispatcher ? "green" : undefined}>{entry.role}</Tag>
        <span>{dispatcher ? "发单号上下文" : "剧本上下文"}</span>
      </header>
      <ScriptRows rows={entry.rows} imageTextsByFileToken={imageTextsByFileToken} />
    </article>
  );
}

function RoleEntry(props: {
  entry: Extract<ReviewTimelineEntry, { kind: "role" }>;
  assignmentResult: AssignmentResult;
  originalAssignment?: RoleAssignmentItem;
  selectedWxid?: string;
  selectedMember?: ScriptRoleReviewMemberSnapshot;
  changed: boolean;
  membersLoading: boolean;
  imageTextsByFileToken: Record<string, string>;
  onEdit: () => void;
  onRestore: () => void;
}) {
  const {
    entry,
    assignmentResult,
    originalAssignment,
    selectedWxid,
    selectedMember,
    changed,
    membersLoading,
    imageTextsByFileToken,
    onEdit,
    onRestore,
  } = props;
  const role = assignmentResult.tempRoles.find((item) => item.tempRoleId === entry.tempRoleId);
  const persona = assignmentResult.personas.find((item) => item.tempRoleId === entry.tempRoleId);

  return (
    <article className={`script-review-role${changed ? " is-changed" : ""}`}>
      <header className="script-review-role-header">
        <div className="script-review-role-heading">
          <div>
            <Tag color="blue">气氛员{entry.tempRoleId}</Tag>
            {role ? <Tag>{getScriptContentBeatLabel(role.contentBeat)}</Tag> : null}
            {changed ? <Tag color="gold">已人工修改</Tag> : <Tag color="green">保留 AI 匹配</Tag>}
          </div>
          {originalAssignment ? (
            <span>
              AI 原匹配：{originalAssignment.number || "无编号"} {originalAssignment.nickname}
              <strong>{originalAssignment.score} 分</strong>
            </span>
          ) : (
            <span className="is-error">缺少 AI 原始匹配记录</span>
          )}
        </div>
        <div className="script-review-role-actions">
          {changed ? (
            <Button size="small" icon={<ReloadOutlined />} onClick={onRestore}>
              恢复原匹配
            </Button>
          ) : null}
          <Button type="primary" size="small" icon={<EditOutlined />} onClick={onEdit}>
            修改成员
          </Button>
        </div>
      </header>

      <div className="script-review-role-body">
        <section className="script-review-script-pane">
          <div className="script-review-section-title">
            <strong>完整话术</strong>
            <span>{entry.rows.length} 行</span>
          </div>
          <ScriptRows rows={entry.rows} imageTextsByFileToken={imageTextsByFileToken} />
          {persona ? (
            <div className="script-review-role-persona">
              <span>角色画像</span>
              <p>{persona.personaSummary || "未生成角色画像"}</p>
              <div>
                {persona.toneTags.map((tag) => (
                  <Tag className="script-review-role-tag" key={`tone-${tag}`}>{tag}</Tag>
                ))}
                {persona.topicTags.map((tag) => (
                  <Tag className="script-review-role-tag" key={`topic-${tag}`}>{tag}</Tag>
                ))}
              </div>
            </div>
          ) : null}
          {originalAssignment?.reason ? (
            <div className="script-review-ai-reason">
              <span>AI 原始总评</span>
              <p>{originalAssignment.reason}</p>
            </div>
          ) : null}
          {originalAssignment?.scoreBreakdown.length ? (
            <div className="script-review-score-breakdown">
              <span>AI 六维评分</span>
              <div>
                {originalAssignment.scoreBreakdown.map((dimension) => (
                  <Tooltip key={dimension.key} title={dimension.reason}>
                    <span
                      className={dimension.score < 0 ? "is-negative" : ""}
                      tabIndex={0}
                      aria-label={`${dimension.label} ${dimension.score}${dimension.maxScore > 0 ? ` / ${dimension.maxScore}` : ""}，${dimension.reason}`}
                    >
                      <em>{dimension.label}</em>
                      <strong>{dimension.score}</strong>
                      {dimension.maxScore > 0 ? <small>/{dimension.maxScore}</small> : null}
                    </span>
                  </Tooltip>
                ))}
              </div>
            </div>
          ) : null}
        </section>

        <section className="script-review-member-pane">
          <div className="script-review-section-title">
            <strong>当前气氛员</strong>
            <span>{changed ? "人工选择" : "AI 原匹配"}</span>
          </div>
          {membersLoading ? (
            <div className="script-review-member-loading">
              <Spin description="加载成员资料" />
            </div>
          ) : selectedMember ? (
            <ScriptMemberProfile
              member={selectedMember}
              selectedLabel={changed ? "人工选择" : "AI 原匹配"}
            />
          ) : selectedWxid ? (
            <MissingMember wxid={selectedWxid} originalAssignment={originalAssignment} />
          ) : (
            <Empty description="尚未选择气氛员" />
          )}
        </section>
      </div>
    </article>
  );
}

export function ScriptAssignmentReviewPanel({
  open,
  assignmentResult,
  parsedRows,
  sourceFingerprint,
  sheetTitle,
  overwriteMode,
  imageTextsByFileToken,
  onClose,
  onConfirmStart,
  onConfirmed,
  onConfirmError,
}: ScriptAssignmentReviewPanelProps) {
  const [view, setView] = useState<ReviewView>("all");
  const [memberFilterWxid, setMemberFilterWxid] = useState<string>();
  const [editingTempRoleId, setEditingTempRoleId] = useState<string | null>(null);
  const review = useScriptAssignmentReview({
    open,
    assignmentResult,
    sourceFingerprint,
    sheetTitle,
    overwriteMode,
    imageTextsByFileToken,
    onConfirmStart,
    onConfirmed,
    onConfirmError,
  });

  useEffect(() => {
    if (!open) {
      setEditingTempRoleId(null);
      setView("all");
    }
  }, [open]);

  const timeline = useMemo(
    () => buildReviewTimeline({ assignmentResult, parsedRows, overwriteMode }),
    [assignmentResult, overwriteMode, parsedRows]
  );
  const visibleTimeline = useMemo(() => {
    const base = view === "changed"
      ? timeline.filter(
          (entry) =>
            entry.kind === "role" && review.changedTempRoleIds.has(entry.tempRoleId)
        )
      : timeline;
    if (!memberFilterWxid) return base;
    return base.filter(
      (entry) =>
        entry.kind === "role" &&
        review.draftSelections[entry.tempRoleId] === memberFilterWxid
    );
  }, [memberFilterWxid, review.changedTempRoleIds, review.draftSelections, timeline, view]);
  const reviewLineCount = useMemo(
    () => timeline.reduce((total, entry) => total + entry.rows.length, 0),
    [timeline]
  );
  const memberFilterOptions = useMemo(
    () =>
      [...review.members]
        .filter((member) => member.number.trim())
        .sort((left, right) => left.number.localeCompare(right.number, "zh-CN", { numeric: true }))
        .map((member) => ({
          value: member.wxid,
          label: `${member.number} ${member.nickname || "未命名气氛员"}`,
        })),
    [review.members]
  );
  const filteredMember = memberFilterWxid
    ? review.memberByWxid.get(memberFilterWxid)
    : undefined;
  const editingRole = assignmentResult.tempRoles.find(
    (role) => role.tempRoleId === editingTempRoleId
  );
  const selectedEditingWxid = editingTempRoleId
    ? review.draftSelections[editingTempRoleId]
    : undefined;
  const originalEditingWxid = editingTempRoleId
    ? review.originalAssignmentByRoleId.get(editingTempRoleId)?.wxid
    : undefined;
  const missingScriptRoleCount = new Set(
    timeline.flatMap((entry) =>
      entry.kind === "role" && entry.rows.length === 0 ? [entry.tempRoleId] : []
    )
  ).size;
  const blockingError =
    review.confirmError ??
    review.membersError ??
    (missingScriptRoleCount > 0
      ? new Error(`${missingScriptRoleCount} 个角色缺少可展示的剧本行，请重新读取后再审查`)
      : null) ??
    (!review.membersLoading && !review.selectionValidation.ok
      ? new Error(review.selectionValidation.error)
      : null);

  const closePanel = () => {
    if (review.submitting) return;
    setEditingTempRoleId(null);
    onClose();
  };

  return (
    <>
      <Modal
        title={
          <div className="script-review-modal-title">
            <span>匹配结果审查</span>
            <small>{sheetTitle || assignmentResult.sheetId}</small>
          </div>
        }
        open={open}
        width="96vw"
        style={{ top: "4vh", paddingBottom: 0 }}
        footer={null}
        closable={!review.submitting}
        keyboard={!review.submitting}
        onCancel={closePanel}
        destroyOnHidden
        mask={{ closable: false, blur: true }}
        styles={{
          container: {
            height: "92vh",
            maxWidth: "96vw",
            padding: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
          header: { margin: 0, padding: "14px 20px", borderBottom: "1px solid #e2e8f0" },
          body: { minHeight: 0, flex: 1, padding: 0, overflow: "hidden" },
        }}
      >
        <div className="script-review-workbench">
          <div className="script-review-toolbar">
            <div className="script-review-stats">
              <span>
                <strong>{assignmentResult.tempRoles.length}</strong> 个角色
              </span>
              <span>
                <strong>{reviewLineCount}</strong> 行剧本
              </span>
              <span className={review.changedRoleCount > 0 ? "is-changed" : ""}>
                <strong>{review.changedRoleCount}</strong> 项修改
              </span>
              <span>
                <strong>{review.membersLoading ? "…" : review.members.length}</strong> 名候选
              </span>
              {overwriteMode ? <Tag color="gold">覆盖模式</Tag> : <Tag>仅写空白 D 列</Tag>}
              <Tooltip title={sourceFingerprint}>
                <code>{sourceFingerprint.slice(0, 22)}</code>
              </Tooltip>
            </div>
            <div className="script-review-filter-tools">
              <Select
                allowClear
                showSearch
                value={memberFilterWxid}
                options={memberFilterOptions}
                optionFilterProp="label"
                placeholder="按编号筛选成员"
                style={{ width: 210 }}
                onChange={setMemberFilterWxid}
              />
              <Segmented
                size="small"
                value={view}
                options={[
                  { label: `完整剧本 (${timeline.length})`, value: "all" },
                  { label: `仅看改动 (${review.changedRoleCount})`, value: "changed" },
                ]}
                onChange={(value) => setView(value as ReviewView)}
              />
            </div>
          </div>

          {filteredMember ? (
            <div className="script-review-filter-summary">
              <div>
                <strong>正在查看编号 {filteredMember.number}</strong>
                <span>{visibleTimeline.filter((entry) => entry.kind === "role").length} 个角色</span>
              </div>
              <ScriptMemberProfile member={filteredMember} compact selectedLabel="筛选成员" />
            </div>
          ) : null}

          {blockingError ? (
            <div className="script-review-error" role="alert">
              <strong>当前无法确认</strong>
              <span>{blockingError.message}</span>
            </div>
          ) : null}

          {review.draftStorageWarning ? (
            <div className="script-review-warning" role="status">
              <strong>草稿未持久化</strong>
              <span>{review.draftStorageWarning}；当前页面内仍可继续审查并确认。</span>
            </div>
          ) : null}

          <main className="script-review-scroll">
            {visibleTimeline.length > 0 ? (
              <div className="script-review-timeline">
                {visibleTimeline.map((entry) =>
                  entry.kind === "context" ? (
                    <ContextEntry
                      key={entry.key}
                      entry={entry}
                      imageTextsByFileToken={imageTextsByFileToken}
                    />
                  ) : (
                    <RoleEntry
                      key={entry.key}
                      entry={entry}
                      assignmentResult={assignmentResult}
                      originalAssignment={review.originalAssignmentByRoleId.get(entry.tempRoleId)}
                      selectedWxid={review.draftSelections[entry.tempRoleId]}
                      selectedMember={review.memberByWxid.get(
                        review.draftSelections[entry.tempRoleId] ?? ""
                      )}
                      changed={review.changedTempRoleIds.has(entry.tempRoleId)}
                      membersLoading={review.membersLoading}
                      imageTextsByFileToken={imageTextsByFileToken}
                      onEdit={() => setEditingTempRoleId(entry.tempRoleId)}
                      onRestore={() => review.restoreOriginalSelection(entry.tempRoleId)}
                    />
                  )
                )}
              </div>
            ) : (
              <Empty description="当前没有人工修改，切换到完整剧本继续审查" />
            )}
          </main>

          <footer className="script-review-footer">
            <div className="script-review-footer-summary">
              <UserSwitchOutlined className="script-review-footer-icon" />
              <div>
                <strong>
                  已修改 {review.changedRoleCount} / {assignmentResult.tempRoles.length} 个角色
                </strong>
                <span>审查号 {review.reviewId.slice(0, 8) || "准备中"}</span>
              </div>
            </div>
            <div className="script-review-footer-actions">
              <Button disabled={review.submitting} onClick={closePanel}>
                返回
              </Button>
              <Button
                type="primary"
                size="large"
                icon={<CheckCircleOutlined />}
                loading={review.submitting}
                disabled={!review.canConfirm || missingScriptRoleCount > 0}
                onClick={() => void review.confirm()}
              >
                确认并写入飞书
              </Button>
            </div>
          </footer>
        </div>
      </Modal>

      <ScriptMemberPickerModal
        open={open && Boolean(editingTempRoleId)}
        tempRoleLabel={editingRole?.speakerLabel || `气氛员${editingTempRoleId ?? ""}`}
        members={review.members}
        loading={review.membersLoading}
        selectedWxid={selectedEditingWxid}
        originalWxid={originalEditingWxid}
        memberSelectionIssueByWxid={review.memberSelectionIssueByWxid}
        onCancel={() => setEditingTempRoleId(null)}
        onSelect={(wxid) => {
          if (editingTempRoleId) review.changeSelection(editingTempRoleId, wxid);
          setEditingTempRoleId(null);
        }}
      />
    </>
  );
}
